import { Vec2 } from './vec';

/**
 * The shield Prince Nacho's second form makes you hold (spec
 * docs/superpowers/specs/2026-09-11-through-space-and-time-design.md §3).
 *
 * A cousin of `ChipState`: it lands, it burns a fuse, and unresolved it costs
 * lives. The difference is the input — a chip is TAPPED, a shield must be
 * HELD. `held` accumulates every tick the player keeps the pointer down on
 * it, and the hold has to REACH `holdNeeded` before `remaining` reaches 0:
 * starting the hold at 3.5s of a 4s fuse is not enough, which is the plain
 * reading of "if you don't hit it within 4 seconds it deals 2 lives".
 *
 * Releasing does NOT reset `held` — the brief asks the player to hold for two
 * seconds, not to hold without flinching.
 *
 * Sim-owned, so a run stays deterministic: the render layer only draws it and
 * dispatches `holdShield`.
 */
export interface ShieldState {
  id: number;
  pos: Vec2;
  /** Seconds left before the shield costs lives. */
  remaining: number;
  /** The full fuse, so the render layer can scale the warning tint. */
  fuse: number;
  /** Seconds of hold accumulated so far (never reset by a release). */
  held: number;
  /** Hold needed to stop the attack. */
  holdNeeded: number;
  /** Lives an unheld shield costs. */
  lives: number;
  /** True while the player has the pointer down on it. */
  down: boolean;
}

/** Seconds the player must hold a shield to stop the laser. */
export const SHIELD_HOLD = 2;
/** Seconds a shield stays up before it costs lives. */
export const SHIELD_FUSE = 4;
/** Lives an unheld shield costs. */
export const SHIELD_LIVES = 2;

/**
 * Where the three shields of one barrage land, in order.
 *
 * "In the middle of the screen" as a BAND, not a single pixel: the lasers are
 * three seconds apart and a shield lives four, so two can be up at once. One
 * fixed spot would stack them and make the pair unpressable — three spread
 * spots mean every shield of a barrage is always reachable.
 *
 * The FIRST is dead centre (640, 430), because it is the one the player meets
 * first; the other two flank it. All three sit ≥95px from every plot of
 * Through Space and Time, so a 104px disc never buries a tower and its HP bar
 * for the four seconds it is up — pinned by `hubLevels.test.ts` against the
 * level's own plots, the way the ice sculptures are.
 */
export const SHIELD_SPOTS: readonly Vec2[] = [
  { x: 640, y: 430 },
  { x: 320, y: 430 },
  { x: 790, y: 430 },
];

/** A fresh shield at the `n`th spot of a barrage (wraps, though 3 is the most). */
export function createShield(id: number, n: number): ShieldState {
  return {
    id,
    pos: { ...SHIELD_SPOTS[n % SHIELD_SPOTS.length] },
    remaining: SHIELD_FUSE,
    fuse: SHIELD_FUSE,
    held: 0,
    holdNeeded: SHIELD_HOLD,
    lives: SHIELD_LIVES,
    down: false,
  };
}

/** Whether this shield's hold is complete — the laser is stopped. */
export function shieldResolved(s: ShieldState): boolean {
  return s.held >= s.holdNeeded;
}
